import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Link } from "react-router-dom";
import { Nav, NavItem, NavLink, UncontrolledTooltip, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Layout from "../components/layout/layout";
import StyledLoading from "../components/styled/StyledLoading";
import { StyledTable } from "../components/styled/BlacklistStyles";
import { StyledToolbar } from "../components/styled/ToolbarStyles";
import { ToolbarBox } from "../components/styled/DashboardStyles";
import StyledButton from "../components/styled/StyledButton";
import { invoicesSelectors, invoicesActions } from "../redux/invoices";
import { reduxLoaderSelectors } from "../redux/redux-loader";

const LIMIT = 50;

const ACTION_VARIANTS = {
  "fakturownia-invoice": {
    label: "Create FK",
    title: "Create Fakturownia invoice",
    description: "Creates a new invoice in Fakturownia from the Chargebee data and saves it locally.",
    bg: "#1976d2",
    hover: "#1565c0",
  },
  "fakturownia-ksef": {
    label: "Send KSeF",
    title: "Send to KSeF",
    description: "Re-sends the existing Fakturownia invoice to the Polish KSeF government system.",
    bg: "#6a1b9a",
    hover: "#4a148c",
  },
  "invoice-email": {
    label: "Send email",
    title: "Send invoice email",
    description: "Sends the invoice email to the customer via Postmark.",
    bg: "#2e7d32",
    hover: "#1b5e20",
  },
  "chargebee-invoice-fakturownia": {
    label: "Sync FK",
    title: "Sync Chargebee → Fakturownia",
    description: "Creates a Fakturownia invoice directly from this Chargebee invoice without touching the local DB or sending email.",
    bg: "#ef6c00",
    hover: "#e65100",
  },
  "chargebee-creditnote-fakturownia": {
    label: "Sync FK",
    title: "Sync Credit Note → Fakturownia",
    description: "Creates a corrective Fakturownia invoice from this Chargebee credit note.",
    bg: "#ef6c00",
    hover: "#e65100",
  },
};

const ActionButton = ({ action, invoiceId, onTrigger, isLoading, isActive }) => {
  const variant = ACTION_VARIANTS[action];
  const btnId = `act-${action}-${String(invoiceId).replace(/[^a-zA-Z0-9]/g, "-")}`;
  const disabled = isLoading;
  const dim = isLoading && !isActive;

  return (
    <>
      <button
        id={btnId}
        type="button"
        onClick={() => onTrigger(invoiceId, [action])}
        disabled={disabled}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontSize: "11px",
          fontWeight: 500,
          padding: "4px 10px",
          borderRadius: "4px",
          border: "none",
          background: variant.bg,
          color: "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: dim ? 0.4 : 1,
          transition: "background 120ms ease, transform 80ms ease",
          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
        }}
        onMouseEnter={(e) => {
          if (!disabled) e.currentTarget.style.background = variant.hover;
        }}
        onMouseLeave={(e) => {
          if (!disabled) e.currentTarget.style.background = variant.bg;
        }}
      >
        {isActive ? (
          <span style={{ display: "inline-block", width: 10, height: 10, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        ) : null}
        <span>{variant.label}</span>
      </button>
      <UncontrolledTooltip placement="top" target={btnId}>
        <div style={{ textAlign: "left", fontSize: "11px" }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>{variant.title}</div>
          <div style={{ opacity: 0.9 }}>{variant.description}</div>
        </div>
      </UncontrolledTooltip>
    </>
  );
};

const ConfirmModal = ({ pending, onCancel, onConfirm }) => {
  if (!pending) return null;
  const variant = ACTION_VARIANTS[pending.action] || { title: pending.action, description: "", bg: "#1976d2", hover: "#1565c0" };
  return (
    <Modal isOpen={true} toggle={onCancel} modalTransition={{ timeout: 20 }} backdropTransition={{ timeout: 20 }}>
      <ModalHeader toggle={onCancel}>Confirm: {variant.title}</ModalHeader>
      <ModalBody>
        <p style={{ marginBottom: 12 }}>{variant.description}</p>
        <p style={{ marginBottom: 0, fontSize: 13 }}>
          <strong>Target invoice:</strong>{" "}
          <code style={{ background: "#f5f5f5", padding: "2px 6px", borderRadius: 3 }}>{pending.invoiceId}</code>
        </p>
        <p style={{ marginTop: 12, fontSize: 12, color: "#c62828" }}>
          This action is irreversible. Are you sure you want to run it?
        </p>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: "6px 14px",
            fontSize: 12,
            borderRadius: 4,
            border: "1px solid #ccc",
            background: "#fff",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          style={{
            padding: "6px 14px",
            fontSize: 12,
            borderRadius: 4,
            border: "none",
            background: variant.bg,
            color: "#fff",
            cursor: "pointer",
          }}
        >
          Yes, run this action
        </button>
      </ModalFooter>
    </Modal>
  );
};

const Invoices = ({
  list,
  chargebeeList,
  chargebeeCreditNotesList,
  count,
  currentPage,
  source,
  getInvoices,
  getChargebeeInvoices,
  getChargebeeCreditNotes,
  triggerActions,
  triggerCreditNoteActions,
  setCurrentPage,
  setSource,
  getInvoicesState,
  getChargebeeInvoicesState,
  getChargebeeCreditNotesState,
  triggerActionsState,
  triggerCreditNoteActionsState,
}) => {
  const [accountFilter, setAccountFilter] = useState("");
  const [pendingInvoiceId, setPendingInvoiceId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(null); // { invoiceId, action }
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', message }

  const prevLoadingRef = useRef(false);

  const pullData = useCallback(
    (page = currentPage) => {
      if (source === "db") {
        getInvoices(page * LIMIT, LIMIT, accountFilter, "", "", "");
      } else if (source === "chargebee") {
        getChargebeeInvoices(accountFilter, LIMIT);
      } else if (source === "chargebee_cn") {
        getChargebeeCreditNotes(accountFilter, LIMIT);
      }
    },
    [source, currentPage, accountFilter, getInvoices, getChargebeeInvoices, getChargebeeCreditNotes]
  );

  useEffect(() => {
    pullData();
  }, [pullData]);

  const activeTriggerState = source === "chargebee_cn" ? triggerCreditNoteActionsState : triggerActionsState;

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    const isLoading = activeTriggerState.isLoading;
    prevLoadingRef.current = isLoading;

    if (wasLoading && !isLoading) {
      if (activeTriggerState.isError) {
        setFeedback({
          type: "error",
          message: `Action "${pendingAction}" failed: ${activeTriggerState.error || "unknown error"}`,
        });
      } else if (activeTriggerState.isDone) {
        setFeedback({
          type: "success",
          message: `Action "${pendingAction}" completed successfully`,
        });
        pullData();
      }
      setPendingInvoiceId(null);
      setPendingAction(null);
      setTimeout(() => setFeedback(null), 5000);
    }
  }, [activeTriggerState.isLoading, activeTriggerState.isError, activeTriggerState.isDone, activeTriggerState.error, pendingAction, pullData, source]);

  const handlePage = (direction) => {
    const newPage = direction === "next" ? currentPage + 1 : Math.max(0, currentPage - 1);
    setCurrentPage(newPage);
  };

  const handleSourceChange = (newSource) => {
    setSource(newSource);
  };

  const requestTrigger = (invoiceId, actions) => {
    setPendingConfirm({ invoiceId, action: actions[0] });
  };

  const confirmTrigger = () => {
    if (!pendingConfirm) return;
    const { invoiceId, action } = pendingConfirm;
    setPendingInvoiceId(invoiceId);
    setPendingAction(action);
    setFeedback(null);
    if (action === "chargebee-creditnote-fakturownia") {
      triggerCreditNoteActions(invoiceId, [action]);
    } else {
      triggerActions(invoiceId, [action]);
    }
    setPendingConfirm(null);
  };

  const cancelTrigger = () => setPendingConfirm(null);

  const isTriggering = (invoiceId, action) =>
    triggerActionsState.isLoading && pendingInvoiceId === invoiceId && pendingAction === action;

  const isLoading = source === "db" ? getInvoicesState.isLoading : source === "chargebee" ? getChargebeeInvoicesState.isLoading : getChargebeeCreditNotesState.isLoading;
  const invoices = source === "db" ? list : source === "chargebee" ? chargebeeList : chargebeeCreditNotesList;
  const totalPages = Math.ceil(count / LIMIT);

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <ConfirmModal pending={pendingConfirm} onCancel={cancelTrigger} onConfirm={confirmTrigger} />
      <StyledToolbar>
        <ToolbarBox>
          <p>Invoices</p>
          {source === "db" && <p>{count}</p>}
        </ToolbarBox>
      </StyledToolbar>

      <div style={{ padding: "0 20px", marginBottom: "16px" }}>
        <Nav tabs>
          <NavItem>
            <NavLink
              style={{ cursor: "pointer", color: source === "db" ? "#007bff" : "#666" }}
              active={source === "db"}
              onClick={() => handleSourceChange("db")}
            >
              Our Invoices
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              style={{ cursor: "pointer", color: source === "chargebee" ? "#007bff" : "#666" }}
              active={source === "chargebee"}
              onClick={() => handleSourceChange("chargebee")}
            >
              Chargebee Invoices
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              style={{ cursor: "pointer", color: source === "chargebee_cn" ? "#007bff" : "#666" }}
              active={source === "chargebee_cn"}
              onClick={() => handleSourceChange("chargebee_cn")}
            >
              Chargebee Credit Notes
            </NavLink>
          </NavItem>
        </Nav>

        <div style={{ display: "flex", gap: "8px", margin: "16px 0" }}>
          <input
            type="text"
            placeholder="Filter by Account ID"
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && pullData(0)}
            style={{ padding: "6px 12px", border: "1px solid #ccc", borderRadius: "4px", width: "300px" }}
          />
          <StyledButton as="button" onClick={() => pullData(0)} style={{ padding: "6px 16px" }}>
            Search
          </StyledButton>
        </div>
      </div>

      {feedback && (
        <div
          style={{
            margin: "0 20px 16px 20px",
            padding: "10px 14px",
            borderRadius: "4px",
            fontSize: "13px",
            background: feedback.type === "success" ? "#e8f5e9" : "#ffebee",
            color: feedback.type === "success" ? "#2e7d32" : "#c62828",
            border: `1px solid ${feedback.type === "success" ? "#a5d6a7" : "#ef9a9a"}`,
          }}
        >
          {feedback.message}
        </div>
      )}

      {isLoading && <StyledLoading />}

      {!isLoading && invoices.length === 0 && (
        <p style={{ padding: "20px", textAlign: "center", color: "#999" }}>No invoices found</p>
      )}

      {!isLoading && invoices.length > 0 && (
        <div style={{ padding: "0 20px" }}>
          <StyledTable>
            <thead>
              <tr>
                {source === "db" ? (
                  <>
                    <th>Fakturownia</th>
                    <th>Invoice ID</th>
                    <th>Account</th>
                    <th>Chargebee ID</th>
                    <th>Amount</th>
                    <th>Currency</th>
                    <th>Kind</th>
                    <th>Gov Status</th>
                    <th>Email Status</th>
                    <th>Payment Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </>
                ) : source === "chargebee" ? (
                  <>
                    <th>ID</th>
                    <th>Fakturownia</th>
                    <th>Customer</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Currency</th>
                    <th>Date</th>
                    <th>Amount Paid</th>
                    <th>Amount Due</th>
                    <th>Actions</th>
                  </>
                ) : (
                  <>
                    <th>ID</th>
                    <th>Fakturownia</th>
                    <th>Customer</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Currency</th>
                    <th>Date</th>
                    <th>Ref Invoice</th>
                    <th>Actions</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {source === "db" ? (
                invoices.map((inv) => (
                    <tr key={inv.invoice_id}>
                      <td style={{ fontSize: "12px" }}>
                        {inv.fakturownia_number || (inv.fakturownia_id > 0 ? inv.fakturownia_id : "-")}
                      </td>
                      <td style={{ fontSize: "12px" }}>{inv.invoice_id}</td>
                      <td style={{ fontSize: "12px" }}>
                        {inv.account_id ? (
                          <Link to={`/accounts/${inv.account_id}`}>{inv.account_id}</Link>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td style={{ fontSize: "12px" }}>{inv.chargebee_id}</td>
                      <td>{(inv.amount / 100).toFixed(2)}</td>
                      <td>{inv.currency}</td>
                      <td>{inv.kind || "-"}</td>
                      <td>
                        <span style={{
                          padding: "2px 6px",
                          borderRadius: "3px",
                          fontSize: "11px",
                          background: inv.gov_status === "demo_ok" || inv.gov_status === "ok" ? "#e8f5e9" : inv.gov_status ? "#fff3e0" : "#f5f5f5",
                          color: inv.gov_status === "demo_ok" || inv.gov_status === "ok" ? "#2e7d32" : "#666",
                        }}>
                          {inv.gov_status || "-"}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          padding: "2px 6px",
                          borderRadius: "3px",
                          fontSize: "11px",
                          background: inv.email_status === "sent" ? "#e8f5e9" : inv.email_status === "error" ? "#ffebee" : "#fff3e0",
                          color: inv.email_status === "sent" ? "#2e7d32" : inv.email_status === "error" ? "#c62828" : "#e65100",
                        }}>
                          {inv.email_status}
                        </span>
                      </td>
                      <td style={{ fontSize: "12px" }}>{inv.payment_status || "-"}</td>
                      <td style={{ fontSize: "12px" }}>
                        {inv.creation_date ? new Date(inv.creation_date).toLocaleDateString() : "-"}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                          {(!inv.fakturownia_id || inv.fakturownia_id === 0) && inv.gov_status !== "no_ksef" && (
                            <ActionButton
                              action="fakturownia-invoice"
                              invoiceId={inv.invoice_id}
                              onTrigger={requestTrigger}
                              isLoading={triggerActionsState.isLoading}
                              isActive={isTriggering(inv.invoice_id, "fakturownia-invoice")}
                            />
                          )}
                          {inv.fakturownia_id > 0 && inv.gov_status !== "ok" && inv.gov_status !== "demo_ok" && inv.gov_status !== "no_ksef" && (
                            <ActionButton
                              action="fakturownia-ksef"
                              invoiceId={inv.invoice_id}
                              onTrigger={requestTrigger}
                              isLoading={triggerActionsState.isLoading}
                              isActive={isTriggering(inv.invoice_id, "fakturownia-ksef")}
                            />
                          )}
                          {inv.email_status !== "sent" && inv.gov_status !== "no_ksef" && (inv.gov_status === "ok" || inv.gov_status === "demo_ok") && (
                            <ActionButton
                              action="invoice-email"
                              invoiceId={inv.invoice_id}
                              onTrigger={requestTrigger}
                              isLoading={triggerActionsState.isLoading}
                              isActive={isTriggering(inv.invoice_id, "invoice-email")}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
              ) : source === "chargebee" ? (
                invoices.map((inv) => (
                    <tr key={inv.id}>
                      <td style={{ fontSize: "12px" }}>{inv.id}</td>
                      <td style={{ fontSize: "12px" }}>
                        {inv.fakturownia_number || (inv.fakturownia_id > 0 ? inv.fakturownia_id : "-")}
                      </td>
                      <td style={{ fontSize: "12px" }}>
                        {inv.customer_id ? (
                          <Link to={`/accounts/${inv.customer_id}`}>{inv.customer_id}</Link>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{inv.status}</td>
                      <td>{(inv.total / 100).toFixed(2)}</td>
                      <td>{inv.currency}</td>
                      <td>{new Date(inv.date * 1000).toLocaleDateString()}</td>
                      <td>{(inv.amount_paid / 100).toFixed(2)}</td>
                      <td>{(inv.amount_due / 100).toFixed(2)}</td>
                      <td>
                        {(!inv.has_mirror || !inv.fakturownia_id) && (
                          <ActionButton
                            action="chargebee-invoice-fakturownia"
                            invoiceId={"inv_" + inv.id}
                            onTrigger={requestTrigger}
                            isLoading={triggerActionsState.isLoading}
                            isActive={isTriggering("inv_" + inv.id, "chargebee-invoice-fakturownia")}
                          />
                        )}
                        {inv.has_mirror && !!inv.fakturownia_id && (
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: "3px",
                              fontSize: "11px",
                              background: inv.mirror_source === "livesession" ? "#e3f2fd" : "#eeeeee",
                              color: inv.mirror_source === "livesession" ? "#1565c0" : "#616161",
                              border: `1px solid ${inv.mirror_source === "livesession" ? "#90caf9" : "#bdbdbd"}`,
                            }}
                          >
                            Already synced
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
              ) : (
                invoices.map((cn) => (
                    <tr key={cn.id}>
                      <td style={{ fontSize: "12px" }}>{cn.id}</td>
                      <td style={{ fontSize: "12px" }}>
                        {cn.fakturownia_number || (cn.fakturownia_id > 0 ? cn.fakturownia_id : "-")}
                      </td>
                      <td style={{ fontSize: "12px" }}>
                        {cn.customer_id ? (
                          <Link to={`/accounts/${cn.customer_id}`}>{cn.customer_id}</Link>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{cn.status}</td>
                      <td>{(cn.total / 100).toFixed(2)}</td>
                      <td>{cn.currency}</td>
                      <td>{new Date(cn.date * 1000).toLocaleDateString()}</td>
                      <td style={{ fontSize: "12px" }}>{cn.reference_invoice_id || "-"}</td>
                      <td>
                        {(!cn.has_mirror || !cn.fakturownia_id) && (
                          <ActionButton
                            action="chargebee-creditnote-fakturownia"
                            invoiceId={cn.id}
                            onTrigger={requestTrigger}
                            isLoading={activeTriggerState.isLoading}
                            isActive={isTriggering(cn.id, "chargebee-creditnote-fakturownia")}
                          />
                        )}
                        {cn.has_mirror && !!cn.fakturownia_id && (
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: "3px",
                              fontSize: "11px",
                              background: cn.mirror_source === "livesession" ? "#e3f2fd" : "#eeeeee",
                              color: cn.mirror_source === "livesession" ? "#1565c0" : "#616161",
                              border: `1px solid ${cn.mirror_source === "livesession" ? "#90caf9" : "#bdbdbd"}`,
                            }}
                          >
                            Already synced
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </StyledTable>

          {source === "db" && totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "16px", margin: "16px 0" }}>
              <StyledButton as="button" onClick={() => handlePage("prev")} disabled={currentPage === 0}>
                Previous
              </StyledButton>
              <span>
                Page {currentPage + 1} of {totalPages}
              </span>
              <StyledButton as="button" onClick={() => handlePage("next")} disabled={currentPage >= totalPages - 1}>
                Next
              </StyledButton>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default connect(
  (state) => ({
    list: invoicesSelectors.getInvoicesList(state),
    chargebeeList: invoicesSelectors.getChargebeeInvoicesList(state),
    chargebeeCreditNotesList: invoicesSelectors.getChargebeeCreditNotesList(state),
    count: invoicesSelectors.getInvoicesCount(state),
    currentPage: invoicesSelectors.getCurrentPage(state),
    source: invoicesSelectors.getSource(state),
    getInvoicesState: reduxLoaderSelectors.getLoadingState(state, invoicesActions.GET_INVOICES),
    getChargebeeInvoicesState: reduxLoaderSelectors.getLoadingState(state, invoicesActions.GET_CHARGEBEE_INVOICES),
    getChargebeeCreditNotesState: reduxLoaderSelectors.getLoadingState(state, invoicesActions.GET_CHARGEBEE_CREDIT_NOTES),
    triggerActionsState: reduxLoaderSelectors.getLoadingState(state, invoicesActions.TRIGGER_ACTIONS),
    triggerCreditNoteActionsState: reduxLoaderSelectors.getLoadingState(state, invoicesActions.TRIGGER_CREDIT_NOTE_ACTIONS),
  }),
  (dispatch) =>
    bindActionCreators(
      {
        getInvoices: invoicesActions.getInvoices,
        getChargebeeInvoices: invoicesActions.getChargebeeInvoices,
        getChargebeeCreditNotes: invoicesActions.getChargebeeCreditNotes,
        triggerActions: invoicesActions.triggerActions,
        triggerCreditNoteActions: invoicesActions.triggerCreditNoteActions,
        setCurrentPage: (page) => ({ type: invoicesActions.CURRENT_PAGE, payload: page }),
        setSource: (source) => ({ type: invoicesActions.SET_SOURCE, payload: source }),
      },
      dispatch
    )
)(Layout(Invoices));